/**
 * Instrument Preset Manager
 * 
 * Manages instrument presets including saving, loading, deleting, and persistence.
 * Handles both default presets (provided by the instrument database) and user-created
 * presets (stored in localStorage).
 * 
 * @example
 * ```typescript
 * import { instrumentPresetManager } from './InstrumentPresetManager';
 * 
 * // Get presets for an instrument
 * const presets = instrumentPresetManager.getPresetsForInstrument('square');
 * 
 * // Save a new preset
 * const newPreset = instrumentPresetManager.savePreset({
 *   instrumentId: 'square',
 *   name: 'My Custom Lead',
 *   description: 'Bright and punchy',
 *   parameters: { adsr: { attack: 0.01, decay: 0.1, sustain: 0.7, release: 0.1 } }
 * });
 * 
 * // Apply a preset
 * const params = instrumentPresetManager.applyPreset(presetId);
 * ```
 */

import { InstrumentType } from '../../types/composition';
import { InstrumentPreset, InstrumentParameters } from '../../types/instruments';
import { instrumentDatabase } from './InstrumentDatabase';

const STORAGE_KEY = 'instrument-presets';

/**
 * InstrumentPresetManager class - manages preset operations and persistence.
 * 
 * This class is implemented as a singleton to ensure consistent preset management
 * across the application. It automatically loads user presets from localStorage
 * on initialization and persists changes automatically.
 * 
 * @class
 */
class InstrumentPresetManager {
  private userPresets: Map<string, InstrumentPreset>;

  constructor() {
    this.userPresets = new Map();
    this.loadPresetsFromStorage();
  }

  /**
   * Get all presets for a specific instrument (default + user-created).
   * 
   * Returns both default presets from the instrument database and user-created
   * presets stored in localStorage.
   * 
   * @param instrumentId - The instrument type identifier
   * @returns Array of all presets for the instrument (default presets first, then user presets)
   * 
   * @example
   * ```typescript
   * const presets = instrumentPresetManager.getPresetsForInstrument('square');
   * console.log(`Found ${presets.length} presets for Square Lead`);
   * ```
   */
  getPresetsForInstrument(instrumentId: InstrumentType): InstrumentPreset[] {
    try {
      const instrument = instrumentDatabase.getInstrument(instrumentId);
      if (!instrument) {
        console.warn(`Instrument not found: ${instrumentId}`);
        return [];
      }

      // Get default presets from the instrument database
      const defaultPresets = instrument.presets || [];

      // Get user-created presets for this instrument
      const userPresetsForInstrument = Array.from(this.userPresets.values()).filter(
        (preset) => preset.instrumentId === instrumentId
      );

      // Combine and return
      return [...defaultPresets, ...userPresetsForInstrument];
    } catch (error) {
      console.error(`Error getting presets for instrument ${instrumentId}:`, error);
      return [];
    }
  }

  /**
   * Get a specific preset by ID.
   * 
   * Searches both user presets and default presets across all instruments.
   * 
   * @param presetId - The unique preset identifier
   * @returns The preset if found, undefined otherwise
   * 
   * @example
   * ```typescript
   * const preset = instrumentPresetManager.getPreset('square-bright');
   * if (preset) {
   *   console.log(`Found preset: ${preset.name}`);
   * }
   * ```
   */
  getPreset(presetId: string): InstrumentPreset | undefined {
    // Check user presets first
    if (this.userPresets.has(presetId)) {
      return this.userPresets.get(presetId);
    }

    // Check default presets in all instruments
    const allInstruments = instrumentDatabase.getAllInstruments();
    for (const instrument of allInstruments) {
      const preset = instrument.presets.find((p) => p.id === presetId);
      if (preset) {
        return preset;
      }
    }

    return undefined;
  }

  /**
   * Save a new user preset.
   * 
   * Creates a new user preset with a unique ID and persists it to localStorage.
   * The preset is automatically marked as user-created and timestamped.
   * 
   * @param preset - The preset data (without id, isDefault, isUserCreated, createdAt)
   * @returns The complete preset object with generated ID and metadata
   * @throws Error if preset data is invalid or instrument doesn't exist
   * 
   * @example
   * ```typescript
   * const newPreset = instrumentPresetManager.savePreset({
   *   instrumentId: 'square',
   *   name: 'My Custom Lead',
   *   description: 'Bright and punchy lead sound',
   *   parameters: {
   *     adsr: { attack: 0.01, decay: 0.1, sustain: 0.7, release: 0.1 },
   *     filter: { frequency: 3000, resonance: 2 }
   *   }
   * });
   * ```
   */
  savePreset(preset: Omit<InstrumentPreset, 'id' | 'isDefault' | 'isUserCreated' | 'createdAt'>): InstrumentPreset {
    try {
      // Validate preset data
      if (!preset.instrumentId || !preset.name || !preset.parameters) {
        throw new Error('Invalid preset data: missing required fields');
      }

      // Validate instrument exists
      const instrument = instrumentDatabase.getInstrument(preset.instrumentId);
      if (!instrument) {
        throw new Error(`Invalid instrument ID: ${preset.instrumentId}`);
      }

      // Generate unique ID
      const id = this.generatePresetId(preset.instrumentId, preset.name);

      // Create full preset object
      const fullPreset: InstrumentPreset = {
        ...preset,
        id,
        isDefault: false,
        isUserCreated: true,
        createdAt: new Date(),
      };

      // Store in memory
      this.userPresets.set(id, fullPreset);

      // Persist to localStorage
      this.savePresetsToStorage();

      return fullPreset;
    } catch (error) {
      console.error('Error saving preset:', error);
      throw error; // Re-throw so caller can handle
    }
  }

  /**
   * Update an existing user preset
   */
  updatePreset(presetId: string, updates: Partial<InstrumentPreset>): InstrumentPreset | null {
    const preset = this.userPresets.get(presetId);
    
    if (!preset || !preset.isUserCreated) {
      // Can only update user-created presets
      return null;
    }

    // Update preset
    const updatedPreset: InstrumentPreset = {
      ...preset,
      ...updates,
      id: presetId, // Preserve ID
      isDefault: false, // Preserve flags
      isUserCreated: true,
      createdAt: preset.createdAt ?? new Date(), // Preserve creation date or use current
    };

    this.userPresets.set(presetId, updatedPreset);
    this.savePresetsToStorage();

    return updatedPreset;
  }

  /**
   * Delete a user preset.
   * 
   * Only user-created presets can be deleted. Default presets cannot be removed.
   * Changes are automatically persisted to localStorage.
   * 
   * @param presetId - The unique preset identifier
   * @returns true if preset was deleted, false if preset not found or is a default preset
   * 
   * @example
   * ```typescript
   * const deleted = instrumentPresetManager.deletePreset('square-my-custom-1234567890');
   * if (deleted) {
   *   console.log('Preset deleted successfully');
   * }
   * ```
   */
  deletePreset(presetId: string): boolean {
    const preset = this.userPresets.get(presetId);
    
    if (!preset || !preset.isUserCreated) {
      // Can only delete user-created presets
      return false;
    }

    this.userPresets.delete(presetId);
    this.savePresetsToStorage();
    return true;
  }

  /**
   * Apply a preset and return its parameters.
   * 
   * Retrieves the preset and returns a deep copy of its parameters to prevent
   * accidental mutations of the original preset data.
   * 
   * @param presetId - The unique preset identifier
   * @returns The preset parameters if found, null otherwise
   * 
   * @example
   * ```typescript
   * const params = instrumentPresetManager.applyPreset('square-bright');
   * if (params) {
   *   // Apply parameters to instrument
   *   voiceFactory.updateParameters(voice, params);
   * }
   * ```
   */
  applyPreset(presetId: string): InstrumentParameters | null {
    try {
      const preset = this.getPreset(presetId);
      
      if (!preset) {
        console.warn(`Preset not found: ${presetId}`);
        return null;
      }

      // Validate preset parameters before returning
      if (!preset.parameters || typeof preset.parameters !== 'object') {
        console.error(`Invalid preset parameters for ${presetId}`);
        return null;
      }

      // Return a deep copy of the parameters
      return JSON.parse(JSON.stringify(preset.parameters));
    } catch (error) {
      console.error(`Error applying preset ${presetId}:`, error);
      return null;
    }
  }

  /**
   * Get all user-created presets
   */
  getAllUserPresets(): InstrumentPreset[] {
    return Array.from(this.userPresets.values());
  }

  /**
   * Export all user presets as JSON string.
   * 
   * Useful for backing up presets or sharing them with others.
   * 
   * @returns JSON string containing all user-created presets
   * 
   * @example
   * ```typescript
   * const json = instrumentPresetManager.exportPresets();
   * // Save to file or copy to clipboard
   * navigator.clipboard.writeText(json);
   * ```
   */
  exportPresets(): string {
    const presets = Array.from(this.userPresets.values());
    return JSON.stringify(presets, null, 2);
  }

  /**
   * Import presets from JSON string.
   * 
   * Validates each preset before importing and generates new IDs to avoid conflicts.
   * Returns a summary of successful and failed imports with error messages.
   * 
   * @param data - JSON string containing preset data
   * @returns Object with success count, failed count, and array of error messages
   * 
   * @example
   * ```typescript
   * const result = instrumentPresetManager.importPresets(jsonString);
   * console.log(`Imported ${result.success} presets`);
   * if (result.failed > 0) {
   *   console.error('Errors:', result.errors);
   * }
   * ```
   */
  importPresets(data: string): { success: number; failed: number; errors: string[] } {
    const result = {
      success: 0,
      failed: 0,
      errors: [] as string[],
    };

    try {
      const presets = JSON.parse(data) as InstrumentPreset[];

      if (!Array.isArray(presets)) {
        result.errors.push('Invalid format: expected an array of presets');
        result.failed = 1;
        return result;
      }

      for (const preset of presets) {
        try {
          // Validate preset structure
          if (!this.validatePreset(preset)) {
            result.failed++;
            result.errors.push(`Invalid preset structure: ${(preset as any)?.name || 'unknown'}`);
            continue;
          }

          // Check if instrument exists
          const instrument = instrumentDatabase.getInstrument(preset.instrumentId);
          if (!instrument) {
            result.failed++;
            result.errors.push(`Unknown instrument: ${preset.instrumentId}`);
            continue;
          }

          // Generate new ID to avoid conflicts
          const newId = this.generatePresetId(preset.instrumentId, preset.name);
          
          const importedPreset: InstrumentPreset = {
            ...preset,
            id: newId,
            isDefault: false,
            isUserCreated: true,
            createdAt: new Date(),
          };

          this.userPresets.set(newId, importedPreset);
          result.success++;
        } catch (error) {
          result.failed++;
          result.errors.push(`Error importing preset: ${error instanceof Error ? error.message : 'unknown error'}`);
        }
      }

      if (result.success > 0) {
        this.savePresetsToStorage();
      }
    } catch (error) {
      result.failed = 1;
      result.errors.push(`Failed to parse JSON: ${error instanceof Error ? error.message : 'unknown error'}`);
    }

    return result;
  }

  /**
   * Clear all user presets
   */
  clearAllUserPresets(): void {
    this.userPresets.clear();
    this.savePresetsToStorage();
  }

  /**
   * Generate a unique preset ID
   */
  private generatePresetId(instrumentId: InstrumentType, name: string): string {
    const timestamp = Date.now();
    const sanitizedName = name.toLowerCase().replace(/[^a-z0-9]/g, '-');
    return `${instrumentId}-${sanitizedName}-${timestamp}`;
  }

  /**
   * Validate preset structure
   */
  private validatePreset(preset: any): preset is InstrumentPreset {
    return (
      preset &&
      typeof preset === 'object' &&
      typeof preset.instrumentId === 'string' &&
      typeof preset.name === 'string' &&
      typeof preset.description === 'string' &&
      preset.parameters &&
      typeof preset.parameters === 'object'
    );
  }

  /**
   * Load presets from localStorage
   */
  private loadPresetsFromStorage(): void {
    try {
      const stored = localStorage.getItem(STORAGE_KEY);
      if (!stored) {
        return;
      }

      const presets = JSON.parse(stored) as InstrumentPreset[];
      
      if (!Array.isArray(presets)) {
        console.warn('Invalid preset data in localStorage');
        return;
      }

      // Restore presets to map
      for (const preset of presets) {
        if (this.validatePreset(preset)) {
          // Restore Date object
          if (preset.createdAt) {
            preset.createdAt = new Date(preset.createdAt);
          }
          this.userPresets.set(preset.id, preset);
        }
      }
    } catch (error) {
      console.error('Failed to load presets from storage:', error);
    }
  }

  /**
   * Save presets to localStorage
   */
  private savePresetsToStorage(): void {
    try {
      const presets = Array.from(this.userPresets.values());
      localStorage.setItem(STORAGE_KEY, JSON.stringify(presets));
    } catch (error) {
      console.error('Failed to save presets to storage:', error);
    }
  }
}

// Export singleton instance
export const instrumentPresetManager = new InstrumentPresetManager();
export default instrumentPresetManager;
